import React from 'react';
import { motion } from 'framer-motion';
import {
  Scissors,
  CheckCircle2,
  Sparkles,
  Shirt,
  Box,
  Truck,
  Layers,
  Activity
} from 'lucide-react';

const stageIcons = {
  '01': Scissors,
  '02': Activity,
  '03': Sparkles,
  '04': Shirt,
  '05': Layers,
  '06': CheckCircle2,
  '07': Box,
  '08': Truck
};

const ProcessStage = ({ stage, isReversed = false }) => {
  const Icon = stageIcons[stage.step] || Sparkles;

  return (
    <motion.div
      initial={{ opacity: 0, y: 50 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-60px' }}
      transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
        gap: '40px',
        alignItems: 'center',
        background: '#ffffff',
        border: '1px solid var(--color-border)',
        boxShadow: '0 12px 36px rgba(15, 23, 42, 0.06)',
        borderRadius: '16px',
        padding: 'clamp(24px, 4vw, 48px)',
        position: 'relative',
        overflow: 'hidden'
      }}
    >
      {/* Background Subtle Grid Effect */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          backgroundImage: 'radial-gradient(rgba(224, 0, 135, 0.08) 1px, transparent 1px)',
          backgroundSize: '24px 24px',
          opacity: 0.5,
          pointerEvents: 'none'
        }}
      />

      {/* Left Column: Visual Stage Graphic & Scan Overlay */}
      <div style={{ position: 'relative', borderRadius: '12px', overflow: 'hidden', minHeight: '320px', border: '1px solid var(--color-border)' }}>
        <motion.img
          src={stage.image}
          alt={stage.heading}
          whileHover={{ scale: 1.05 }}
          transition={{ duration: 0.6, ease: 'easeOut' }}
          style={{
            width: '100%',
            height: '100%',
            maxHeight: '400px',
            objectFit: 'cover',
            display: 'block'
          }}
        />

        {/* Dynamic Scanning & Stitch Line Keyframe Overlays */}
        {stage.step === '01' && (
          <div
            style={{
              position: 'absolute',
              top: '30%',
              left: 0,
              width: '100%',
              height: '2px',
              background: 'linear-gradient(90deg, transparent, #e00087, transparent)',
              boxShadow: '0 0 12px #e00087'
            }}
          />
        )}

        {stage.step === '03' && (
          <svg style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', pointerEvents: 'none' }}>
            <path
              d="M 20 50 Q 150 120 300 80 T 450 150"
              fill="none"
              stroke="#e00087"
              strokeWidth="3"
              strokeDasharray="8 6"
            />
          </svg>
        )}

        {stage.step === '06' && (
          <div
            style={{
              position: 'absolute',
              inset: '20px',
              border: '1px dashed rgba(224, 0, 135, 0.6)',
              borderRadius: '8px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              pointerEvents: 'none'
            }}
          >
            <div style={{ background: 'rgba(255, 255, 255, 0.95)', padding: '6px 14px', borderRadius: '4px', fontSize: '11px', color: 'var(--accent-magenta)', fontWeight: 800, boxShadow: '0 4px 12px rgba(15, 23, 42, 0.1)' }}>
              QC HUD SCAN ACTIVE • 100% SPEC VERIFIED
            </div>
          </div>
        )}

        <div
          style={{
            position: 'absolute',
            top: '16px',
            left: '16px',
            background: 'rgba(255, 255, 255, 0.95)',
            border: '1px solid rgba(224, 0, 135, 0.4)',
            padding: '6px 14px',
            borderRadius: '99px',
            fontSize: '11px',
            fontWeight: 800,
            color: 'var(--accent-magenta)',
            letterSpacing: '0.08em',
            boxShadow: '0 4px 12px rgba(15, 23, 42, 0.08)'
          }}
        >
          {stage.badge}
        </div>
      </div>

      {/* Right Column: Stage Information & Specifications */}
      <div style={{ position: 'relative', zIndex: 2 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '14px', marginBottom: '16px' }}>
          <div
            style={{
              width: '44px',
              height: '44px',
              borderRadius: '10px',
              background: 'linear-gradient(135deg, var(--accent-purple) 0%, var(--accent-magenta) 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#ffffff',
              boxShadow: '0 4px 16px var(--accent-glow)'
            }}
          >
            <Icon size={22} />
          </div>
          <span style={{ fontSize: '14px', fontFamily: 'var(--font-heading)', fontWeight: 800, color: 'var(--accent-magenta)', letterSpacing: '0.1em' }}>
            STAGE {stage.step} OF 08
          </span>
        </div>

        <h2 style={{ fontSize: 'clamp(24px, 3vw, 36px)', fontFamily: 'var(--font-heading)', fontWeight: 900, color: 'var(--text-main)', marginBottom: '10px', textTransform: 'uppercase' }}>
          {stage.heading}
        </h2>

        <p style={{ fontSize: '16px', fontWeight: 700, color: 'var(--accent-purple)', marginBottom: '16px' }}>
          "{stage.subheading}"
        </p>

        <p style={{ fontSize: '14px', color: 'var(--text-muted)', lineHeight: '1.6', marginBottom: '24px' }}>
          {stage.description}
        </p>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '32px' }}>
          {stage.details.map((detail, idx) => (
            <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '14px', color: 'var(--text-muted)' }}>
              <CheckCircle2 size={16} style={{ color: 'var(--accent-magenta)', flexShrink: 0 }} />
              <span style={{ fontWeight: 600 }}>{detail}</span>
            </div>
          ))}
        </div>
      </div>
    </motion.div>
  );
};

export default ProcessStage;
